import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/i18n/I18nProvider";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NewsletterForm({ variant = "hero" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!name.trim() || name.trim().length < 2) errs.name = t("newsletter.error_name");
    if (!EMAIL_RE.test(email.trim())) errs.email = t("newsletter.error_email");
    if (!consent) errs.consent = t("newsletter.error_consent");
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    // Client-only demo: store lead locally so the collection is not lost.
    try {
      const leads = JSON.parse(localStorage.getItem("elq_leads") || "[]");
      leads.push({ name: name.trim(), email: email.trim(), at: new Date().toISOString() });
      localStorage.setItem("elq_leads", JSON.stringify(leads));
    } catch (e) {}
    // Redirect to French-only Thank You page as required
    navigate("/thank_you");
  };

  const isHero = variant === "hero";

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className={
        isHero
          ? "bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm"
          : "bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8"
      }
      data-testid={`newsletter-form-${variant}`}
    >
      <div className={isHero ? "" : "text-white"}>
        <p
          className={`text-xs uppercase tracking-[0.24em] font-semibold mb-2 ${
            isHero ? "text-blue-900" : "text-amber-400"
          }`}
        >
          {t("newsletter.eyebrow")}
        </p>
        <h3
          className={`text-xl md:text-2xl font-semibold mb-1 ${
            isHero ? "text-slate-900" : "text-white"
          }`}
        >
          {t("newsletter.title")}
        </h3>
        <p className={`text-sm mb-5 ${isHero ? "text-slate-600" : "text-slate-300"}`}>
          {t("newsletter.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Field
          id={`nl-${variant}-name`}
          label={t("newsletter.name_label")}
          placeholder={t("newsletter.name_placeholder")}
          value={name}
          onChange={setName}
          error={errors.name}
          dark={!isHero}
          testId="newsletter-name-input"
          autoComplete="name"
        />
        <Field
          id={`nl-${variant}-email`}
          type="email"
          label={t("newsletter.email_label")}
          placeholder={t("newsletter.email_placeholder")}
          value={email}
          onChange={setEmail}
          error={errors.email}
          dark={!isHero}
          testId="newsletter-email-input"
          autoComplete="email"
        />

        <label
          className={`flex items-start gap-3 text-sm ${isHero ? "text-slate-600" : "text-slate-300"}`}
        >
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-amber-500"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="newsletter-consent-checkbox"
          />
          <span>{t("newsletter.consent_label")}</span>
        </label>
        {errors.consent && (
          <p className="text-xs text-red-500 -mt-2" data-testid="newsletter-error-consent">
            {errors.consent}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm py-3 rounded-md disabled:opacity-70"
          data-testid="newsletter-submit-button"
        >
          {submitting ? t("newsletter.submitting") : t("newsletter.submit")}
        </button>

        <p className={`text-xs ${isHero ? "text-slate-500" : "text-slate-400"}`}>
          {t("newsletter.gdpr_note")}
        </p>
      </div>
    </form>
  );
}

function Field({ id, label, type = "text", placeholder, value, onChange, error, dark, testId, autoComplete }) {
  return (
    <div>
      <label
        htmlFor={id}
        className={`block text-xs font-semibold uppercase tracking-widest mb-1.5 ${
          dark ? "text-slate-300" : "text-slate-700"
        }`}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className={`w-full px-3.5 py-2.5 rounded-md border text-sm outline-none focus:ring-2 focus:ring-amber-500/40 ${
          dark
            ? "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
        }`}
        data-testid={testId}
      />
      {error && (
        <p className="text-xs text-red-500 mt-1" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

export default NewsletterForm;
